/* SOLE — collection-card lifestyle hover.
   Lives in its own file, NOT in sole-collection.js, because the card markup and sole-collection.css
   also render on /pages/promotions (snippets/sole-page-promotions.liquid), where sole-collection.js is
   not loaded. When this behaviour lived there, that page emitted 7 dual cards whose hover images never
   hydrated. Ship the feature's JS wherever the feature's CSS goes.
*/
/* ---- lifestyle card hover layer ----
   Two jobs: hydrate the deferred hover images without hurting LCP, and provide the touch equivalent
   of the desktop hover.

   WHY THE SRC IS DEFERRED. The hover <img> ships with data-src, not src. `loading="lazy"` did not
   defer it: on a desktop collection page the cards are at or near the viewport, so the browser began
   all six hover images at ~485ms, ahead of the LCP image, and LCP measured 4016ms -> 4624ms on a
   throttled run (1.6Mbps / 4x CPU, 4 samples each, non-overlapping). Hydrating at idle after load
   takes them off the critical path; hydrating on pointerover/focusin covers a hover that beats idle.
   Measured after the fix in collection-card-hover/qa/2026-08-31.

   SAFETY:
   - If this script never runs the cards are simply the single-image cards they are today. The hover
     layer is opacity:0 and srcless, so a failure here is invisible rather than broken.
   - The scroll reveal bails on any device that HAS hover (desktop already has the CSS behaviour) and
     on reduced motion. The CSS carries its own reduced-motion backstop.
   - Fires ONCE per card per page view (WeakSet) so a card scrolling in and out does not blink.
   - NOTE on sortGrid/reflowConceptA: those relocate card nodes between #heroGrid and #restGrid. That
     is safe without a re-scan, because IntersectionObserver tracks the ELEMENT, not its position.
     Only newly CREATED dual cards would need one, and nothing creates them (the finder builds .fcard
     markup from CMPDATA, not .tc-card). */
(function () {
  /* Either layer can be the deferred one — whichever is HOVER-ONLY carries data-src. On a hover-first
     card that is .tc-card__lifestyle; under lifestyle_first the resting image is the plate and the
     studio shot becomes .tc-card__studio[data-src]. Query both so this file does not encode a
     direction the Liquid is free to invert. */
  var DEFERRED = '.tc-card__lifestyle[data-src], .tc-card__studio[data-src]';
  var imgs = [].slice.call(document.querySelectorAll(DEFERRED));

  function hydrate(img) {
    if (!img || !img.dataset.src) return;
    // Keep the resting layer visible until the alternate image can actually paint.
    img.dataset.loading = '';
    function ready() { delete img.dataset.loading; }
    img.addEventListener('load', function () {
      if (img.decode) img.decode().then(ready).catch(ready);
      else ready();
    }, { once: true });
    if (img.dataset.srcset) { img.srcset = img.dataset.srcset; delete img.dataset.srcset; }
    img.src = img.dataset.src;
    delete img.dataset.src;
    if (img.complete && img.naturalWidth > 0) ready();
  }
  function hydrateCard(media) {
    if (media) hydrate(media.querySelector(DEFERRED));
  }
  // Bulk: once the page is loaded and the main thread is free.
  function kick() { imgs.forEach(hydrate); }
  function schedule() {
    if ('requestIdleCallback' in window) requestIdleCallback(kick, { timeout: 3000 });
    else setTimeout(kick, 1200);
  }

  /* ---- HYDRATION HALF — deferred layers only ----
     THIS IS NOT AN EARLY RETURN FROM THE WHOLE IIFE, and must never become one again. It used to be
     `if (!imgs.length) return;` sitting above everything, which gated TWO independent features on one
     test. Under `lifestyle_first` the hover layer ships a real src, so a page whose dual cards are all
     lifestyle-first has zero [data-src] nodes — the script bailed and the TOUCH block below never ran.
     Desktop hover is pure CSS so the failure was invisible on a laptop, but touch users got no reveal
     at all. That is live on /pages/promotions today (every card there passes the flag), and it would
     have spread to every collection page the moment they flip.
     Only these listeners and the idle pass depend on `imgs`. The touch block depends on
     .tc-card__media--dual, which every dual card carries in either direction. */
  if (imgs.length) {
    // On demand: a hover or a keyboard focus that lands before the idle pass.
    // pointerover/focusin bubble; pointerenter/focus do not, so delegation needs these two.
    document.addEventListener('pointerover', function (e) {
      var m = e.target.closest && e.target.closest('.tc-card__media--dual');
      if (m) hydrateCard(m);
    }, { passive: true });
    document.addEventListener('focusin', function (e) {
      var m = e.target.closest && e.target.closest('.tc-card__media--dual');
      if (m) hydrateCard(m);
    });
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
  }

  /* ---- touch equivalent of the hover ----
     NOTE on the name: on a lifestyle-first card `.is-lifestyle` flashes the STUDIO shot, because the
     class means "show the other layer", not "show the lifestyle layer". The CSS at
     sole-collection.css handles both directions. Renaming it would churn the CSS, this file and the
     spec for no behavioural gain, so the misnomer stays documented rather than fixed. */
  if (!window.matchMedia || !('IntersectionObserver' in window)) return;
  if (window.matchMedia('(hover: hover)').matches) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var media = document.querySelectorAll('.tc-card__media--dual');
  if (!media.length) return;

  var HOLD = 1500;
  var done = new WeakSet();
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting || done.has(e.target)) return;
      var el = e.target;
      done.add(el);
      io.unobserve(el);
      hydrateCard(el);                        // make sure there is something to fade to
      el.classList.add('is-lifestyle');
      setTimeout(function () { el.classList.remove('is-lifestyle'); }, HOLD);
    });
  }, { threshold: 0.55 });

  [].forEach.call(media, function (el) { io.observe(el); });
})();
